import React from 'react';
import { useTranslation } from 'react-i18next';

// assets
import { General } from 'icons/untitledui';

// components
import PrivacyCard from 'appExplainers/components/Privacy';

// hooks
import {
  useUser,
  useUpdateIdentityMutation,
  useUpdateCommunicationPreferencesMutation,
} from 'shared/hooks/api/user';
import useUserLanguage from 'shared/hooks/useUserLanguage';
import { usePosthogCapture } from 'onboarding/posthogCapture/_index';

// translations
import privacyEn from 'onboarding/translations/privacy_en';
import privacyFr from 'onboarding/translations/privacy_fr';

// styles
import StyledMarkdown from 'shared/StyledMarkdown';
import * as Buttons from 'theme/components/Buttons.style';
import * as Modal from 'theme/components/Modal.style';
import * as Styled from 'onboarding/styles/SlidePrivacy.style';

interface SlidePrivacyProps {
  isLearnMoreOpen: boolean;
  setIsLearnMoreOpen: (open: boolean) => void;
}

function SlidePrivacy({
  isLearnMoreOpen,
  setIsLearnMoreOpen,
}: SlidePrivacyProps): JSX.Element {
  const { t } = useTranslation('pageOnboarding');
  const language = useUserLanguage();
  const privacy = language === 'en' ? privacyEn : privacyFr;

  const onboarding = usePosthogCapture();
  const { data: user } = useUser();
  const { mutate: updateIdentity } = useUpdateIdentityMutation();
  const { mutate: updateCommunicationPreferences } =
    useUpdateCommunicationPreferencesMutation();

  const handleLearnMore = () => {
    onboarding.privacyLearnMoreOpened();
    setIsLearnMoreOpen(true);
  };
  const handleLearnMoreClose = () => setIsLearnMoreOpen(false);

  const handleContinue = () => {
    updateIdentity({
      nickname: user.first_name,
      pronouns: ['-'],
    });

    updateCommunicationPreferences({
      communication_preference: 'phone_call',
      feedback_mode: 'virtual',
      feedback_timing: 'immediate',
    });

    onboarding.privacyAccepted();
  };

  return (
    <Styled.Slide>
      <Styled.Title>{t('SlidePrivacy.title')}</Styled.Title>

      <Styled.ContentArea>
        <Styled.ScrollableArea>
          <StyledMarkdown content={privacy} />
        </Styled.ScrollableArea>
        <Styled.Fade />
      </Styled.ContentArea>

      <Styled.Buttons>
        <Buttons.Primary onClick={handleContinue} stretch>
          {t('Buttons.continue')}
        </Buttons.Primary>
        <Buttons.SecondaryLight onClick={handleLearnMore} stretch>
          {t('Buttons.learnMore')}
        </Buttons.SecondaryLight>
      </Styled.Buttons>

      {isLearnMoreOpen && (
        <Modal.Container
          onClose={handleLearnMoreClose}
          open={isLearnMoreOpen}
          PaperComponent={Modal.Card}
        >
          <Modal.Content>
            <General.XClose
              onClick={handleLearnMoreClose}
              size={25}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                cursor: 'pointer',
              }}
            />
            <PrivacyCard />
          </Modal.Content>
        </Modal.Container>
      )}
    </Styled.Slide>
  );
}

export default SlidePrivacy;
